import { sameCompanyFaq } from "@/lib/brand";
import { createFileRoute } from "@tanstack/react-router";
import { hourlyMinimumFaq } from "@/lib/booking-minimums";
import SeoLandingPage, {
  buildFaqSchema,
  buildLocalBusinessSchema,
} from "@/components/SeoLandingPage";
import heroImage from "@/assets/sprinter-starlight-blue.jpg?format=webp&quality=72&w=1400&imagetools";
import midImage from "@/assets/sprinter-limo-1.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryA from "@/assets/sprinter-executive-1.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryB from "@/assets/limo-style-sprinter-party-bus-lounge-bar.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryC from "@/assets/luxury-suburban-suv-captain-seats-atlanta.jpg?format=webp&quality=72&w=1400&imagetools";

const PAGE_URL = "https://atlantaluxurysprinters.com/sprinter-limo-cost-atlanta";
const PAGE_TITLE = "Limo Prices in Atlanta (2026) | Sprinter Limo From $250/Hour";
const PAGE_DESC =
  "How much is a limo in Atlanta? Our chauffeured Mercedes-Benz Sprinter Limousine starts at $250/hour. See what moves the price, then get your exact quote in writing.";

const faqs = [
  {
    q: "How much does a Sprinter limo cost in Atlanta?",
    a: "Our chauffeured Mercedes-Benz Sprinter Limousine begins at $250 per hour, with most evenings quoting between $250 and $375 per hour depending on the vehicle, the date, and how long you hold it. Airport and private-terminal transfers are quoted as a fixed door-to-door rate instead of hourly. Every number is confirmed in writing before you reserve, we do not surprise clients with a total.",
  },
  hourlyMinimumFaq(),
  sameCompanyFaq(),
  {
    q: "How is my price set?",
    a: "Custom quotes are given upon request, based on your date, hours and route. The vehicle and chauffeur are yours exclusively for your booking, we do not stack two clients into one evening.",
  },
  {
    q: "What does your rate pay for?",
    a: "Properly maintained vehicles, commercial insurance and experienced, background-checked chauffeurs who have been with my family for years. We own every vehicle in our fleet, and I'll walk you through your quote personally.",
  },
  {
    q: "What is included in the hourly rate?",
    a: "The vehicle, a vetted professional chauffeur, fuel, detailing before your reservation, chilled water and stemware, garage and staging time, and unlimited stops inside your block. Gratuity, tolls, parking, and any wait time beyond your reserved block are the only items quoted separately.",
  },
  {
    q: "Do you offer flat rates instead of hourly?",
    a: "For airport, FBO, and long-distance work, yes, a fixed door-to-door rate confirmed in writing, unaffected by traffic. Hourly as-directed is used when the vehicle stays with you across a night out, a wedding, a venue tour, or an executive day.",
  },
  {
    q: "Do you require a deposit?",
    a: "A card is held to confirm the reservation. Corporate and repeat clients can move to an account with consolidated monthly billing and no card required per trip.",
  },
  {
    q: "Can you quote a wedding, concert night, or full-day charter?",
    a: "Yes, and those are priced as a planned block rather than an estimate. Send the date, the pickup address, the passenger count, and the schedule; we return a written figure the same day, usually within a couple of hours.",
  },
];

export const Route = createFileRoute("/sprinter-limo-cost-atlanta")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      {
        name: "keywords",
        content:
          "how much does a sprinter limo cost in atlanta, sprinter limo cost atlanta, sprinter limo rental price atlanta, luxury sprinter hourly rate atlanta, mercedes sprinter limousine cost",
      },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { property: "og:image", content: heroImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESC },
      { name: "twitter:image", content: heroImage },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildLocalBusinessSchema({
            url: PAGE_URL,
            name: "Atlanta Luxury Sprinters, Sprinter Limousine Rates",
            image: heroImage,
            description: PAGE_DESC,
            serviceType: "Luxury Sprinter Limousine Service",
            areaServed: [
              { type: "City", name: "Atlanta" },
              { type: "City", name: "Buckhead" },
              { type: "City", name: "Alpharetta" },
              { type: "Airport", name: "Hartsfield-Jackson Atlanta International Airport" },
            ],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SeoLandingPage
      eyebrow="Rates • Metro Atlanta • From $250 / Hour"
      h1="How Much Does a Sprinter Limo Cost in Atlanta?"
      intro="You want to know the cost before you plan the evening. Our Mercedes-Benz Sprinter Limousines start at $250/hour. Text me your date and itinerary, and I'll send a custom quote in writing."
      showPricingTerms
      heroImage={heroImage}
      heroAlt="Mercedes-Benz Sprinter Limousine with starlight ceiling ready for an Atlanta evening"
      midImage={midImage}
      midAlt="Interior of a Mercedes-Benz Sprinter Limousine in Atlanta with lounge seating and wet bar"
      sectionOneTitle="Your Rate, Explained"
      sectionOneBody={[
        "Sprinters start at $250/hour. A typical Saturday evening in Buckhead lands between $250 and $375/hour, depending on the vehicle, date and hours. Airport and private-terminal transfers are quoted flat, door to door.",
        "Your rate pays for properly insured vehicles and experienced chauffeurs who know how to care for your guests. We own and operate our own vehicles.",
        "Tell me whether you're planning a wedding, Masters week, a holiday or an evening out. I'll check the date myself and put every number in writing before you book.",
      ]}
      bulletsTitle="What's Included"
      bulletsIntro="Your written quote makes the inclusions clear."
      bullets={[
        "Current-model Mercedes-Benz Sprinter, detailed before every reservation",
        "Vetted, background-checked, uniformed professional chauffeur",
        "Exclusive use of the vehicle for your entire block, never shared, never double-booked",
        "Fuel, staging, garage and positioning time built in",
        "Chilled water, stemware, and cabin prepared to your preferences",
        "Unlimited stops within your reserved hours",
        "Direct line to our team, twenty-four hours, with a human answering",
        "Full commercial insurance and licensed livery authority",
        "Absolute discretion, no names, no photos, no stories",
      ]}
      coverageTitle="Where These Rates Apply"
      coverageIntro="Metro Atlanta and the addresses our clients actually keep."
      coverage={[
        "Buckhead",
        "Midtown & Downtown Atlanta",
        "Sandy Springs & Dunwoody",
        "Alpharetta & Milton",
        "Roswell & Johns Creek",
        "Brookhaven & Chamblee",
        "Vinings & Smyrna",
        "Druid Hills & Decatur",
        "Hartsfield-Jackson (ATL)",
        "PDK & Fulton County Executive",
        "Cobb County International",
        "Lake Oconee & North Georgia",
      ]}
      routeTable={{
        title: "Atlanta Sprinter & Chauffeur Rate Ranges",
        intro:
          "Planning ranges, not a menu. Your written quote is exact and does not move after you approve it. Custom quotes are given upon request.",
        rows: [
          {
            route: "Mercedes-Benz Sprinter Limousine (up to 14)",
            distance: "Hourly",
            time: "From $250 / hr",
            notes: "Starlight ceiling, lounge seating, wet bar. Weekend and event dates price higher.",
          },
          {
            route: "Executive Sprinter (up to 13)",
            distance: "Hourly",
            time: "From $250 / hr",
            notes: "Facing captain's chairs, worktable, quiet cabin for executive days.",
          },
          {
            route: "Suburban SUV (up to 6 captain seats or 7 bench seats)",
            distance: "Hourly",
            time: "Custom quote on request",
            notes: "Small-party discretion; often paired with a Sprinter for larger groups.",
          },
          {
            route: "ATL airport transfer (in-town)",
            distance: "Flat rate",
            time: "Quoted door to door",
            notes: "Meet-and-greet, flight tracking, and wait time included in the figure.",
          },
          {
            route: "Private jet / FBO transfer (PDK, FTY, RYY)",
            distance: "Flat rate",
            time: "Quoted door to door",
            notes: "Planeside where the FBO permits; chauffeur staged before wheels down.",
          },
          {
            route: "Evening out, dinner, event, night in Buckhead",
            distance: "Typical block",
            time: "4–6 hours",
            notes: "Your vehicle and chauffeur stay with you between stops.",
          },
          {
            route: "Wedding day coverage",
            distance: "Typical block",
            time: "6–10 hours",
            notes: "Planned to the timeline and priced as one block, not as separate trips.",
          },
          {
            route: "Full-day executive charter",
            distance: "Typical block",
            time: "8–12 hours",
            notes: "One vehicle, one chauffeur, on site all day. Corporate accounts billed monthly.",
          },
          {
            route: "Long distance (Savannah, Chattanooga, Lake Oconee)",
            distance: "Flat rate",
            time: "Quoted per route",
            notes: "Fixed door-to-door figure including the chauffeur's waiting time.",
          },
        ],
        footnote:
          "Gratuity, tolls, and parking are quoted separately and shown plainly. Holiday, Masters week, and major event dates are released on a limited basis and price accordingly.",
      }}
      detailSections={[
        {
          title: "Why We Publish a Floor Instead of a Bargain",
          body: [
            "A published starting rate is a filter, and we intend it to be one. It means the conversation you have with us begins at logistics rather than negotiation, and it means the client sitting in the cabin next Saturday is not the client who spent an hour trying to talk $40 off the hour.",
            "It also means we never have to protect a low price by cutting something you cannot see: the maintenance interval, the insurance limits, the chauffeur's pay, or the second vehicle we keep unbooked so that a mechanical problem never becomes your problem.",
          ],
        },
        {
          title: "What Moves the Number",
          list: [
            "Vehicle, the Sprinter Limousine with starlight ceiling and bar prices above the Executive cabin.",
            "Date, Saturdays, holidays, Masters week, wedding season, and stadium event nights are the scarce inventory.",
            "Length, longer blocks reduce the effective hourly figure; short notice does the opposite.",
            "Distance, anything outside the perimeter or overnight out of market is quoted flat.",
            "Standing arrangements, recurring corporate and family accounts are priced as relationships, not as trips.",
          ],
        },
        {
          title: "Atlanta's Best-Kept Secret, and We'd Like It to Stay That Way",
          body: [
            "We do not advertise our client list, and the people who use us most would be unhappy if we did. What we can say is that the reservations that repeat are the ones where nobody outside the vehicle ever knew it happened, the arrival at the side entrance, the jet that landed early, the anniversary nobody photographed.",
            "That is the actual product. The starlight ceiling is nice. Not thinking about the transportation once, all night, is the thing people pay for and the thing they miss when they book on price instead.",
          ],
        },
        {
          title: "How to Get an Exact Figure",
          list: [
            "Send the date, pickup address, passenger count, and rough schedule.",
            "We confirm vehicle availability for that date, some dates are already gone.",
            "You receive a written all-in quote, typically within a couple of hours.",
            "A card holds the reservation; corporate accounts move to monthly billing.",
            "The figure you approved is the figure you pay.",
          ],
        },
      ]}
      gallery={{
        title: "What $250 an Hour Looks Like",
        intro: "The cabins these rates reserve, exclusively, for the length of your block.",
        images: [
          { src: galleryA, alt: "Executive Mercedes-Benz Sprinter interior available for hourly charter in Atlanta", caption: "Executive Sprinter, up to 13" },
          { src: galleryB, alt: "Mercedes-Benz Sprinter Limousine lounge cabin with ambient lighting in Atlanta", caption: "Sprinter Limousine, up to 14" },
          { src: galleryC, alt: "Suburban SUV interior for discreet small-party Atlanta transfers", caption: "Suburban SUV, up to 6 (captain seats) or 7 (bench seats)" },
        ],
      }}
      faqs={faqs}
      faqTitle="Atlanta Sprinter Limo Pricing FAQs"
      relatedTitle="Related Rate & Service Pages"
      related={[
        { to: "/luxury-car-service-rates-atlanta", title: "Luxury Car Service Rates", desc: "Full rate structure, custom quotes, and what's included." },
        { to: "/private-chauffeur-hourly-rates-atlanta", title: "Hourly Chauffeur Rates", desc: "As-directed blocks and standing arrangements." },
        { to: "/sprinter-limo-atlanta", title: "Sprinter Limo Atlanta", desc: "The vehicle itself, cabin, capacity, and features." },
      ]}
      ctaTitle="Text Me for Your Written Quote"
      ctaBody="Send me your date, pickup, group size and planned hours. I'll check availability myself and explain the full quote before you book."
    />
  );
}
